import { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import getOAuthClient, { refreshTokens, isAuthenticated } from '../../../services/e3DAuthService'
import { logger } from '../../../utils/dccLogger'
import { grpAuthStateSelector, isGrpLoggedInSelector } from '../../../redux/selectors/e3DGrpAuthSelectors'
import { updateUserAfterRefresh, logout as logoutAction } from '../../../redux/actions/app/e3DGrpAuth.actions'

const REFRESH_THRESHOLD_MS = 2 * 60 * 1000 // 2 minutes before expiry
const TOKEN_CHECK_INTERVAL_MS = 60 * 1000 // 1 minute check interval

const useE3DTokenRefresh = (is3DExhibitionEnabled: boolean): void => {
    const dispatch = useDispatch()
    const isGrpAuthenticated = useSelector(isGrpLoggedInSelector)
    const e3DGrpAuthState = useSelector(grpAuthStateSelector)
    const intervalRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }
    }, [])

    useEffect(() => {
        if (!is3DExhibitionEnabled || !isGrpAuthenticated) {
            return
        }

        const checkAndRefreshTokens = async () => {
            try {
                if (!isAuthenticated()) {
                    logger.warn('Token check: User is not authenticated, check continues in case refresh is viable.')
                }

                const user = e3DGrpAuthState.user
                if (!user?.expires_at) {
                    logger.warn('Token check: No user or expiry information available')
                    return
                }

                const expiresAtMs = user.expires_at * 1000
                const timeUntilExpiry = expiresAtMs - Date.now()

                if (timeUntilExpiry <= REFRESH_THRESHOLD_MS) {
                    logger.info('Token will expire soon or has expired, attempting refresh...')
                    const oAuthClient = getOAuthClient()
                    if (oAuthClient) {
                        const refreshResult = await refreshTokens(oAuthClient, user, e3DGrpAuthState)
                        if (refreshResult.success && refreshResult.updatedUser) {
                            // Update Redux state with the new user
                            dispatch(updateUserAfterRefresh(refreshResult.updatedUser))
                            logger.info(
                                'Token refresh completed successfully. E3D SDK will pick up fresh tokens natively.',
                            )
                        } else {
                            if (refreshResult.terminal) {
                                logger.warn(
                                    'Token refresh reported terminal error. Dispatching logout to reset E3D authentication state.',
                                )
                                dispatch(logoutAction())
                            } else {
                                // For non-terminal errors, keep existing tokens so that future silent sign-ins can succeed.
                                logger.error(
                                    'Token refresh did not succeed. Preserving existing session for future attempts.',
                                )
                            }
                        }
                    } else {
                        logger.error('OAuth client not available for token refresh')
                    }
                }
            } catch (error) {
                // Avoid aggressively cleaning up tokens here to preserve the ability to recover via later silent sign-ins.
                // Terminal conditions are already handled inside refreshTokens() with a full logout.
                logger.error(`Error during token refresh: ${error}`)
            }
        }

        // Initial check when hook starts
        checkAndRefreshTokens()

        // Set up periodic checks
        intervalRef.current = setInterval(checkAndRefreshTokens, TOKEN_CHECK_INTERVAL_MS)

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }
    }, [is3DExhibitionEnabled, isGrpAuthenticated, e3DGrpAuthState, dispatch])
}

export default useE3DTokenRefresh
